import React, { useState, useRef, useEffect } from "react";
import { Edit, MoreVertical, Bell, Upload, CheckCircle } from "lucide-react";
import Footer from "../LandingPage/Components/Footer";
import CalendarIcon from "../../Images/calendar.svg";
import patient from "../../Images/Patients icon.svg";
import line from "../../Images/line.svg";
import Frame_blue from "../../Images/Frame_blue.svg";
import Frame_yellow from "../../Images/Frame_yellow.svg";
import Frame_virtual from "../../Images/Frame_virtual.svg";
import { API_URL } from "../../config/api";

const superAdminRoles = ["super_admin"];

export default function ProfileCard({ userData }) {
  const [imageError, setImageError] = useState(false);
  const [frontPageVideo, setFrontPageVideo] = useState(null);
  const [frontPageVideoURL, setFrontPageVideoURL] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0); // 0–100
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const { user, dashboard } = userData || {};

  // Detect role
  const userRole = user?.roles?.[0]?.role?.name || user?.roles?.[0]?.name || "";
  const isSuperAdmin = superAdminRoles.includes(userRole);

  // ── Fetch existing front page video on mount (super_admin only) ──
  useEffect(() => {
    if (!isSuperAdmin) return;

    const token = localStorage.getItem("access_token");
    fetch(`${API_URL}/azure-upload/home-page-video`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) return null; // 404 = no video saved yet, that's fine
        return res.json();
      })
      .then((data) => {
        if (data?.video_url) {
          setFrontPageVideoURL(data.video_url);
        }
      })
      .catch(() => {}); // silently ignore network errors
  }, [isSuperAdmin]);

  if (!userData) return null;

  // Normalize stats
  const summary = dashboard?.summary;
  const stats = {
    ongoing_programs:
      summary?.total_ongoing_programs ?? dashboard?.ongoing_programs ?? 0,
    students:
      summary?.total_enrolled_students ?? dashboard?.students ?? 0,
    virtual_classes:
      summary?.total_virtual_classes ?? dashboard?.virtual_classes?.length ?? 0,
    inperson_classes:
      summary?.total_inperson_classes ?? dashboard?.inperson_classes?.length ?? 0,
  };

  /* ---- Helpers ---- */
  const getInitials = (name) => {
    if (!name) return "U";
    return name.trim().split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const userInitials = getInitials(user.full_name);
  const hasAvatar = user.avatar_url && !imageError;

  /* ---- Auto Upload with XHR Progress ---- */
  const uploadVideo = (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setUploadError("Please upload a valid video file.");
      return;
    }

    const token = localStorage.getItem("access_token");
    const formData = new FormData();
    formData.append("file", file);

    setFrontPageVideo(file);
    setFrontPageVideoURL(URL.createObjectURL(file));
    setUploadSuccess(false);
    setUploadError(null);
    setUploadProgress(0);
    setIsUploading(true);

    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    });

    xhr.addEventListener("load", async () => {
      if (xhr.status === 200 || xhr.status === 201) {
        const data = JSON.parse(xhr.responseText);
        console.log("✅ Azure Upload Response →", data);
        console.log("📎 Video URL →", data.url);

        // ✅ FIX: Use API_URL directly — it already contains /azure-upload prefix
        try {
          const dbRes = await fetch(`${API_URL}/azure-upload/home-page-video`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ video_url: data.url }),
          });

          const dbData = await dbRes.json();

          if (!dbRes.ok) {
            throw new Error(dbData.detail || "Failed to save video URL");
          }

          console.log("✅ Saved to DB →", dbData);
          setFrontPageVideoURL(data.url); // Use the permanent Azure URL, not the blob URL
          setUploadSuccess(true);
          setUploadProgress(100);

          // Auto-hide success after 3s
          setTimeout(() => setUploadSuccess(false), 3000);

        } catch (dbErr) {
          console.error("❌ DB save failed →", dbErr.message);
          setUploadError(`Uploaded but failed to save: ${dbErr.message}`);
        }

      } else {
        let errMsg = "Upload failed";
        try {
          const err = JSON.parse(xhr.responseText);
          errMsg = err.detail || errMsg;
        } catch (_) {}
        console.error("❌ Upload failed →", errMsg);
        setUploadError(errMsg);
        setFrontPageVideo(null);
        setFrontPageVideoURL(null);
        setUploadProgress(0);
      }
      setIsUploading(false);
    });

    xhr.addEventListener("error", () => {
      setIsUploading(false);
      console.error("❌ Network error during upload");
      setUploadError("Network error. Please try again.");
      setFrontPageVideo(null);
      setFrontPageVideoURL(null);
      setUploadProgress(0);
    });

    xhr.open("POST", `${API_URL}/azure-upload/pdf-upload`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.send(formData);
  };

  const handleInputChange = (e) => uploadVideo(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    uploadVideo(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  /* ---- Current video source ---- */
  const currentVideoSrc = frontPageVideoURL || user.biovideo_url || null;
  const videoSectionTitle = isSuperAdmin ? "Main Page Video" : "Introduction Video";

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-800">My Profile</h1>
        <button className="flex items-center gap-2 rounded border border-[#4AACC5] px-6 py-2 text-[#4AACC5] hover:bg-teal-50 transition-colors">
          <Edit className="w-4 h-4" />
          <span className="font-medium">Edit</span>
        </button>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200">
              {hasAvatar ? (
                <img
                  src={user.avatar_url}
                  alt={user.full_name}
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full h-full bg-[#4AACC5] flex items-center justify-center text-white font-semibold text-xl">
                  {userInitials}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <div className="bg-[#4AACC5] rounded-lg px-3 py-1 inline-flex w-fit">
                <span className="text-white text-sm font-normal">ID - {user.user_id}</span>
              </div>
              <h2 className="text-zinc-800 text-xl font-bold">{user.full_name}</h2>
            </div>
          </div>

          <div className="flex items-center gap-3.5">
            <div className={`rounded-full px-4 py-1 ${user.is_active ? "bg-emerald-100" : "bg-gray-200"}`}>
              <span className={`${user.is_active ? "text-lime-800" : "text-gray-600"} text-sm`}>
                {user.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <button className="w-9 h-9 bg-white rounded-full border border-gray-200 hover:border-gray-300 flex items-center justify-center">
                <Bell className="w-4 h-4 text-red-600" />
              </button>
              <button className="w-9 h-9 bg-white rounded-full border border-gray-200 hover:border-gray-300 flex items-center justify-center">
                <MoreVertical className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Instructor Info + Video Section */}
      <div className="flex gap-6">

        {/* Left Info Card */}
        <div className="w-[457px] bg-white rounded-lg border p-6">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">

            {/* Left Column */}
            <div className="flex items-start flex-col gap-4">
              <div className="flex flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Admin Name</div>
                <div className="text-zinc-800 text-sm font-medium">{user.full_name || "N/A"}</div>
              </div>

              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Email</div>
                <div className="text-zinc-700 text-sm font-medium break-words">{user.email || "N/A"}</div>
              </div>

              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Specialization</div>
                <div className="text-zinc-700 text-sm font-medium">{user.specialization || "N/A"}</div>
              </div>

              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Department</div>
                <div className="text-zinc-700 text-sm font-medium">{user.department || "N/A"}</div>
              </div>

              {/* Hide Experience for super_admin */}
              {!isSuperAdmin && (
                <div className="flex items-start flex-col gap-0.5">
                  <div className="text-gray-500 text-xs">Experience</div>
                  <div className="text-zinc-700 text-sm font-medium">
                    {user.years_of_experience ? `${user.years_of_experience} years` : "N/A"}
                  </div>
                </div>
              )}

              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Alternate Number</div>
                <div className="text-zinc-700 text-sm font-medium">{user?.alternate_phone || "N/A"}</div>
              </div>
            </div>

            {/* Right Column */}
            <div className="flex flex-col gap-4">
              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Role</div>
                <div className="text-zinc-700 text-sm font-medium">
                  {user.roles?.[0]?.role?.name || "N/A"}
                </div>
              </div>

              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">LinkedIn URL</div>
                {user.linkedin_url ? (
                  <a href={user.linkedin_url} target="_blank" rel="noreferrer"
                    className="text-cyan-700 underline text-sm break-all">
                    {user.linkedin_url}
                  </a>
                ) : (
                  <div className="text-zinc-700 text-sm font-medium">N/A</div>
                )}
              </div>

              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Twitter URL</div>
                {user.twitter_url ? (
                  <a href={user.twitter_url} target="_blank" rel="noreferrer"
                    className="text-cyan-700 underline text-sm break-words">
                    {user.twitter_url}
                  </a>
                ) : (
                  <div className="text-zinc-700 text-sm font-medium">N/A</div>
                )}
              </div>

              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Instagram URL</div>
                {user.instagram_url ? (
                  <a href={user.instagram_url} target="_blank" rel="noreferrer"
                    className="text-cyan-700 underline text-sm break-words">
                    {user.instagram_url}
                  </a>
                ) : (
                  <div className="text-zinc-700 text-sm font-medium">N/A</div>
                )}
              </div>

              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Mobile Number</div>
                <div className="text-zinc-700 text-sm font-medium">{user?.phone || "N/A"}</div>
              </div>

              <div className="flex items-start flex-col gap-0.5">
                <div className="text-gray-500 text-xs">Address</div>
                <div className="text-zinc-700 text-sm font-medium break-words text-left">
                  {[user.address, user.city, user.state, user.country, user.zip_code]
                    .filter(Boolean).join(", ") || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Video + Bio */}
        <div className="w-[711px] bg-white rounded-lg border p-6 flex flex-col gap-6">

          {/* Video Section Title */}
          <div className="flex items-center justify-between">
            <h2 className="text-zinc-800 text-xl font-bold">{videoSectionTitle}</h2>
            {/* Change Video — only for super_admin when a video is already showing */}
            {isSuperAdmin && currentVideoSrc && !isUploading && (
              <button
                onClick={() => {
                  setFrontPageVideo(null);
                  setFrontPageVideoURL(null);
                  setUploadSuccess(false);
                  setUploadError(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                  setTimeout(() => fileInputRef.current?.click(), 0);
                }}
                className="flex items-center gap-2 rounded border border-[#4AACC5] px-4 py-1.5 text-[#4AACC5] text-sm hover:bg-teal-50 transition-colors"
              >
                <Upload className="w-4 h-4" />
                Change Video
              </button>
            )}
          </div>

          {/* Hidden file input */}
          {isSuperAdmin && (
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={handleInputChange}
            />
          )}

          {/* Video Display / Dropzone */}
          {isSuperAdmin ? (
            <>
              {currentVideoSrc ? (
                /* Video preview with overlay progress bar */
                <div className="relative w-full h-80 rounded-[20px] border border-stone-300 overflow-hidden bg-gray-50">
                  <video
                    src={currentVideoSrc}
                    controls
                    className="w-full h-full object-cover rounded-[20px]"
                  >
                    Your browser does not support the video tag.
                  </video>

                  {/* Progress overlay — ONLY on video, while uploading */}
                  {isUploading && (
                    <div className="absolute inset-0 bg-black/50 rounded-[20px] flex flex-col items-center justify-center gap-4 px-8">
                      <span className="text-white text-sm font-medium truncate max-w-xs">{frontPageVideo?.name}</span>
                      <div className="w-full">
                        <div className="flex justify-between text-white text-xs mb-1.5">
                          <span>Uploading...</span>
                          <span className="font-bold">{uploadProgress}%</span>
                        </div>
                        <div className="w-full h-2.5 bg-white/30 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#4AACC5] rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Drag & drop zone */
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full h-80 rounded-[20px] border-2 border-dashed flex flex-col items-center justify-center gap-4 cursor-pointer transition-colors
                    ${isDragging
                      ? "border-[#4AACC5] bg-teal-50"
                      : "border-stone-300 bg-gray-50 hover:border-[#4AACC5] hover:bg-teal-50"
                    }`}
                >
                  <div className="w-16 h-16 rounded-full bg-[#4AACC5]/10 flex items-center justify-center">
                    <Upload className="w-8 h-8 text-[#4AACC5]" />
                  </div>
                  <div className="text-center">
                    <p className="text-base font-medium text-zinc-700">
                      {isDragging ? "Drop your video here" : "Click or drag & drop to upload"}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">MP4, MOV, AVI, WEBM supported</p>
                  </div>
                </div>
              )}

              {/* ── Success message — below video, auto-hides after 3s ── */}
              {uploadSuccess && !isUploading && (
                <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span className="text-emerald-700 text-sm font-medium">
                    Video uploaded and saved successfully!
                  </span>
                </div>
              )}

              {/* ── Error message — below video ── */}
              {uploadError && !isUploading && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-red-700 text-sm font-medium">{uploadError}</span>
                </div>
              )}
            </>
          ) : (
            /* Non-admin: just show the video */
            <div className="w-full h-80 rounded-[20px] border border-stone-300 flex items-center justify-center bg-gray-50">
              {user.biovideo_url ? (
                <video src={user.biovideo_url} controls className="w-full h-full rounded-[20px] object-cover">
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="flex flex-col items-center gap-4 text-gray-500">
                  <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-base font-medium">No video available</p>
                </div>
              )}
            </div>
          )}

          {/* BIO / Experience — hidden for super_admin */}
          {!isSuperAdmin && (
            <>
              <h2 className="text-zinc-800 text-xl font-bold">BIO / Experience</h2>
              <p className="text-neutral-700 text-base leading-8">{user.bio || "N/A"}</p>
            </>
          )}
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <div className="h-36 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
          <div className="flex items-start gap-3">
            <img src={CalendarIcon} alt="" className="w-6 h-6" />
            <h3 className="text-gray-900 text-base font-semibold leading-tight">On Going Programs</h3>
          </div>
          <div className="flex items-end justify-between">
            <span className="text-4xl font-bold text-gray-900">{stats.ongoing_programs}</span>
            <div className="w-28 h-14">
              <img src={Frame_blue} alt="" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>

        <div className="h-36 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
          <div className="flex items-start gap-3">
            <img src={patient} alt="" className="w-6 h-6" />
            <h3 className="text-gray-900 text-base font-semibold leading-tight">Students</h3>
          </div>
          <div className="flex items-end justify-between">
            <span className="text-4xl font-bold text-gray-900">{stats.students}</span>
            <div className="w-28 h-14">
              <img src={line} alt="line chart" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>

        <div className="h-36 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
          <div className="flex items-start gap-3">
            <img src={Frame_virtual} alt="" className="w-6 h-6" />
            <h3 className="text-gray-900 text-base font-semibold leading-tight">Virtual Class</h3>
          </div>
          <div className="flex items-end justify-between">
            <span className="text-4xl font-bold text-gray-900">{stats.virtual_classes}</span>
            <div className="w-28 h-14">
              <img src={Frame_yellow} alt="" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>

        <div className="h-36 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
          <div className="flex items-start gap-3">
            <img src={patient} alt="" className="w-6 h-6" />
            <h3 className="text-gray-900 text-base font-semibold leading-tight">Regular/In-person class</h3>
          </div>
          <div className="flex items-end justify-between">
            <span className="text-4xl font-bold text-gray-900">{stats.inperson_classes}</span>
            <div className="w-28 h-14">
              <img src={line} alt="trend chart" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}